import { Theme } from '@mui/material/styles';
import { Fragment, useCallback, useMemo } from 'react';

import { Divider } from '@/components/Layout/Divider';
import { DrawerProps } from '@/components/Layout/Drawer';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectOperationData } from '@/redux/operation/selectors';
import { selectUserData } from '@/redux/user-data/selectors';

import {
  DocumentSection,
  DocumentType,
  RequiredDocumentSection,
} from '@/types/document';

import { getDataTestId } from '@/utils/browser';
import {
  findActiveDoc,
  formatDocumentTypesList,
  isIDWithoutPhoto,
} from '@/utils/document';

import { SecondCitizenshipSection } from './SecondCitizenship';
import { SecondIDSection } from './SecondIDSection';

import { StyledRequiredDocumentSelectDrawer } from '../Drawers.styled';

type AdditionalIDSelectDrawerProps = {
  sections: RequiredDocumentSection[];
  clientId?: string | number;
} & DrawerProps;

export function AdditionalIDSelectDrawer({
  sections,
  clientId,
  onClose,
  ...props
}: AdditionalIDSelectDrawerProps) {
  const { t } = useTranslate();
  const userData = useAppSelector(selectUserData);
  const operationData = useAppSelector(selectOperationData);
  const { user } = clientId ? operationData : userData;
  // drawerSections will contain SecondID and/or SecondCitizenshipID sections
  const isSecondCitizenshipSection = useCallback(
    (targetSection: RequiredDocumentSection) =>
      String(targetSection.type) === DocumentSection.SecondCitizenshipID,
    [],
  );
  const isSecondIdSectionType = useCallback(
    (targetSection: RequiredDocumentSection) =>
      targetSection.type === DocumentSection.SecondID ||
      isSecondCitizenshipSection(targetSection),
    [isSecondCitizenshipSection],
  );
  const hasDualNationalitySection = useMemo(
    () => sections.some(section => isSecondCitizenshipSection(section)),
    [sections, isSecondCitizenshipSection],
  );

  const getSecondIdGuidance = useCallback(
    (targetSection: RequiredDocumentSection) => {
      if (!isSecondIdSectionType(targetSection) || !user) return null;

      const allDocTypes = (targetSection.groups || []).flatMap(group =>
        group.items.map(item => item.name),
      );

      const photoIdTypes = allDocTypes.filter(type => !isIDWithoutPhoto(type));
      const nonPhotoIdTypes = allDocTypes.filter(type =>
        isIDWithoutPhoto(type),
      );

      const photoIdsList = formatDocumentTypesList(photoIdTypes, t);
      const nonPhotoIdsList = formatDocumentTypesList(nonPhotoIdTypes, t);

      return {
        photoIdsList,
        nonPhotoIdsList,
      };
    },
    [isSecondIdSectionType, t, user],
  );
  const getSecondIdCopy = useCallback(
    (targetSection: RequiredDocumentSection) => {
      if (!isSecondIdSectionType(targetSection) || !user) return undefined;
      if (isSecondCitizenshipSection(targetSection)) {
        return {
          headerKey:
            'moreInfo.requiredDocumentGroup.secondIDHeader.dualNationality',
          guidanceKey:
            'moreInfo.requiredDocumentGroup.secondIDGuidance.dualNationality',
        };
      }

      if (!hasDualNationalitySection && user.isHighRisk) {
        return {
          headerKey: 'moreInfo.requiredDocumentGroup.secondIDHeader.highRisk',
          guidanceKey:
            'moreInfo.requiredDocumentGroup.secondIDGuidance.highRisk',
        };
      }

      const isFamilyBookIdentity =
        findActiveDoc(user.documents, DocumentSection.Identity)?.docType ===
        DocumentType.FamilyBook;

      if (isFamilyBookIdentity) {
        return {
          headerKey:
            'moreInfo.requiredDocumentGroup.secondIDHeader.additionalDocument',
          guidanceKey:
            'moreInfo.requiredDocumentGroup.secondIDGuidance.additionalDocument',
        };
      }

      return {
        headerKey:
          'moreInfo.requiredDocumentGroup.secondIDHeader.nationalitySupport',
        guidanceKey:
          'moreInfo.requiredDocumentGroup.secondIDGuidance.nationalitySupport',
      };
    },
    [
      hasDualNationalitySection,
      isSecondCitizenshipSection,
      isSecondIdSectionType,
      user,
    ],
  );

  const renderSectionByType = (section: RequiredDocumentSection) => {
    const secondIdGuidance = getSecondIdGuidance(section);
    const secondIdCopy = getSecondIdCopy(section);
    switch (section.type) {
      case DocumentSection.SecondID:
        return (
          <SecondIDSection
            secondIdGuidance={secondIdGuidance}
            secondIdCopy={secondIdCopy}
            clientId={clientId}
            onConfirmed={onClose}
          />
        );
      case DocumentSection.SecondCitizenshipID:
        return (
          <SecondCitizenshipSection
            secondIdGuidance={secondIdGuidance}
            secondIdCopy={secondIdCopy}
            clientId={clientId}
            onConfirmed={onClose}
          />
        );
      default:
        return null;
    }
  };

  return (
    <StyledRequiredDocumentSelectDrawer
      {...props}
      {...getDataTestId(`additional-id-select-drawer`)}
      onClose={onClose}
      title={t('documentSection.Second_ID.title')}
      bgColor={(theme: Theme) => theme.palette.neutral[100]}
      headerBgColor={(theme: Theme) => theme.palette.neutral[100]}
    >
      {sections.map((section, sectionIndex) => {
        return (
          <Fragment key={`${section.type}-${sectionIndex}`}>
            {renderSectionByType(section)}
            {sectionIndex < sections.length - 1 && (
              <Divider
                width="100%"
                containerProps={{
                  my: 4,
                }}
              />
            )}
          </Fragment>
        );
      })}
    </StyledRequiredDocumentSelectDrawer>
  );
}
